import { useQuery } from "@tanstack/react-query";
import type {
  ReferralClaimResponseDto,
  ReferralClickResponseDto,
  ReferralPublicCreatorResponseDto,
} from "@artha/api-contracts";
import type { ReferralDashboard, ReferralRewardEntry } from "@artha/types";
import { getApiBaseUrlWithFallback } from "@/lib/apiBaseUrl";
import { apiFetch, type ApiResult } from "@/lib/apiClient";
import { queryKeys } from "@/lib/queryKeys";
import {
  clearReferralAttribution,
  getReferralVisitorId,
  getStoredReferralAttribution,
  storeReferralAttribution,
} from "./referralStorage";

type ApiPayload<T> = T | { data: T };
type RevenueImpactResponseDto = {
  clicks: number;
  applications: number;
  isEstimated: boolean;
  hasMeaningfulActivity: boolean;
  monthName?: string;
};

const unwrapApiData = <T>(res: ApiResult<ApiPayload<T>>): T => {
  const payload = res.data as ApiPayload<T>;
  if (payload && typeof payload === "object" && "data" in (payload as Record<string, unknown>)) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

export const fetchPublicReferralInvite = async (
  creatorUniqueId: string
): Promise<ReferralPublicCreatorResponseDto> => {
  let apiBaseUrl = getApiBaseUrlWithFallback();
  if (apiBaseUrl.startsWith("/")) {
    apiBaseUrl = "https://api.artha.link/api";
  }
  const res = await fetch(`${apiBaseUrl}/referrals/public/${encodeURIComponent(creatorUniqueId)}`, {
    next: { revalidate: 3600 },
  });

  if (!res.ok) {
    throw new Error("Failed to fetch referral invite");
  }

  const json = await res.json();
  return json?.data || json;
};

export const trackReferralClick = async (
  creatorUniqueId: string
): Promise<ReferralClickResponseDto> => {
  const stored = getStoredReferralAttribution();
  const res = await apiFetch<ApiPayload<ReferralClickResponseDto>>("/referrals/click", {
    method: "POST",
    body: JSON.stringify({
      creatorUniqueId,
      attributionToken:
        stored?.creatorUniqueId === creatorUniqueId
          ? stored.attributionToken
          : undefined,
      visitorId: getReferralVisitorId(),
    }),
  });

  if (!res.ok) {
    throw new Error("Failed to track referral click");
  }

  const data = unwrapApiData(res);
  storeReferralAttribution({
    attributionToken: data.attributionToken,
    creatorUniqueId: data.creatorUniqueId,
    expiresAt: data.expiresAt,
  });
  return data;
};

export const fetchPublicRevenueImpact = async (
  creatorId: string
): Promise<RevenueImpactResponseDto> => {
  const res = await apiFetch<ApiPayload<RevenueImpactResponseDto>>(
    `/revenue/public-impact/${encodeURIComponent(creatorId)}`
  );

  if (!res.ok) {
    throw new Error("Failed to fetch public revenue impact");
  }

  return unwrapApiData(res);
};

export const claimReferralAttribution = async (
  attributionToken: string
): Promise<ReferralClaimResponseDto> => {
  const res = await apiFetch<ApiPayload<ReferralClaimResponseDto>>("/referrals/claim", {
    method: "POST",
    body: JSON.stringify({ attributionToken }),
  });

  if (!res.ok) {
    throw new Error("Failed to claim referral attribution");
  }

  return unwrapApiData(res);
};

export const claimStoredReferralAttribution = async (): Promise<ReferralClaimResponseDto | null> => {
  const stored = getStoredReferralAttribution();
  if (!stored) return null;

  const result = await claimReferralAttribution(stored.attributionToken);
  if (result.claimed || result.status === "expired" || result.status === "blocked") {
    clearReferralAttribution();
  }
  return result;
};

export const fetchReferralDashboard = async (): Promise<ReferralDashboard> => {
  const res = await apiFetch<ApiPayload<ReferralDashboard>>("/referrals/me/dashboard");
  if (!res.ok) {
    throw new Error("Failed to fetch referral dashboard");
  }
  return unwrapApiData(res);
};

export const fetchReferralPayouts = async (): Promise<ReferralRewardEntry[]> => {
  const res = await apiFetch<ApiPayload<ReferralRewardEntry[]>>("/referrals/me/payouts");
  if (!res.ok) {
    throw new Error("Failed to fetch referral payouts");
  }
  return unwrapApiData(res);
};

export const usePublicReferralInvite = (creatorUniqueId: string) =>
  useQuery({
    queryKey: queryKeys.referralInvite(creatorUniqueId),
    queryFn: () => fetchPublicReferralInvite(creatorUniqueId),
    enabled: Boolean(creatorUniqueId),
    retry: false,
  });

export const usePublicRevenueImpact = (creatorId?: string) =>
  useQuery({
    queryKey: queryKeys.revenue.publicImpact(creatorId || ""),
    queryFn: () => fetchPublicRevenueImpact(creatorId!),
    enabled: Boolean(creatorId),
    retry: false,
  });

export const useReferralDashboard = () =>
  useQuery({
    queryKey: queryKeys.referralDashboard(),
    queryFn: fetchReferralDashboard,
  });

export const useReferralPayouts = () =>
  useQuery({
    queryKey: queryKeys.referralPayouts(),
    queryFn: fetchReferralPayouts,
  });
